import { toggleHidden } from 'src/utils/dom-toggle';
import { Context, Controller } from '@hotwired/stimulus';

export default class ExpiryDateInputController extends Controller {
  public static targets = [
    'input',
    'expiringSoonWarning',
    'expiredError',
    'optionalStartExpiringSoonWarning',
    'optionalStartExpiredError',
  ];

  public static values = {
    leadTime: Number,
    optionalStartDateInputId: String,
  };

  declare private hasLeadTimeValue: boolean;
  declare private leadTimeValue: number;
  declare private hasOptionalStartDateInputIdValue: boolean;
  declare private optionalStartDateInputIdValue: string;

  declare private inputTarget: HTMLInputElement;
  declare private expiringSoonWarningTarget: HTMLElement;
  declare private expiredErrorTarget: HTMLElement;
  declare private optionalStartExpiringSoonWarningTarget: HTMLElement;
  declare private optionalStartExpiredErrorTarget: HTMLElement;

  public constructor(context: Context) {
    super(context);

    this.onDateChanged = this.onDateChanged.bind(this);
  }

  connect(): void {
    super.connect();

    this.inputTarget.addEventListener('change', this.onDateChanged);

    // Listen to project start date changes if available
    const optionalStartInput = this.getOptionalStartDateInput();
    if (optionalStartInput) {
      optionalStartInput.addEventListener('change', this.onDateChanged);
    }

    // Check initial value and show warnings if needed
    this.checkDate();
  }

  disconnect(): void {
    super.disconnect();

    this.inputTarget.removeEventListener('change', this.onDateChanged);

    const optionalStartInput = this.getOptionalStartDateInput();
    if (optionalStartInput) {
      optionalStartInput.removeEventListener('change', this.onDateChanged);
    }
  }

  private getOptionalStartDateInput(): HTMLInputElement | null {
    if (!this.hasOptionalStartDateInputIdValue) {
      return null;
    }

    const element = document.getElementById(this.optionalStartDateInputIdValue);

    if (!(element instanceof HTMLInputElement)) {
      return null;
    }

    return element;
  }

  private onDateChanged(_: Event) {
    this.checkDate();
  }

  private checkDate(): void {
    const referenceDate = this.inputTarget.valueAsDate;
    const optionalStartInput = this.getOptionalStartDateInput();
    const optionalStartDate = optionalStartInput?.valueAsDate || null;

    // Hide all warnings so they don't stay when a value gets cleared
    toggleHidden(this.expiredErrorTarget, true);
    toggleHidden(this.expiringSoonWarningTarget, true);
    if (this.hasOptionalStartDateInputIdValue) {
      toggleHidden(this.optionalStartExpiredErrorTarget, true);
      toggleHidden(this.optionalStartExpiringSoonWarningTarget, true);
    }

    if (referenceDate) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const differenceFromNow = referenceDate.getTime() - now.getTime();
      const leadTimeDays = this.hasLeadTimeValue ? this.leadTimeValue : 30;
      const leadTimeMs = leadTimeDays * 1000 * 60 * 60 * 24;

      // Priority 1: Check if expired (date is in the past)
      const isExpired = differenceFromNow < 0;
      if (isExpired) {
        toggleHidden(this.expiredErrorTarget, false);
        return;
      }

      // Priority 2: Check optional start date coverage ERROR
      if (optionalStartDate) {
        const showOptionalStartError =
          referenceDate.getTime() < optionalStartDate.getTime();

        if (showOptionalStartError) {
          toggleHidden(this.optionalStartExpiredErrorTarget, false);
          return;
        }
      }

      // Priority 3: Check if expiring soon (within lead time from now)
      const isExpiringSoon =
        differenceFromNow >= 0 && differenceFromNow <= leadTimeMs;
      if (isExpiringSoon) {
        toggleHidden(this.expiringSoonWarningTarget, false);
        return;
      }

      // Priority 4: Check optional start date coverage WARNING
      if (optionalStartDate) {
        const differenceFromOptionalStart =
          referenceDate.getTime() - optionalStartDate.getTime();
        const showOptionalStartWarning =
          differenceFromOptionalStart <= leadTimeMs;

        if (showOptionalStartWarning) {
          toggleHidden(this.optionalStartExpiringSoonWarningTarget, false);
          return;
        }
      }
    }
  }
}
